import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';

import { DEFAULT_FORMATTING_LOCALE } from './formattingLocaleDefaults';

/**
 * i18n language codes are translation keys (e.g. `'en'`, `'de'`).
 * `Intl.DateTimeFormat('en')` produces US formatting (MM/DD/YYYY);
 * map bare `'en'` through {@link DEFAULT_FORMATTING_LOCALE} for European conventions.
 */
const FORMATTING_LOCALE_MAP: Record<string, string> = {
  en: DEFAULT_FORMATTING_LOCALE,
};

function resolveFormattingLocales(language: string): string | string[] {
  if (language.length > 0) {
    return FORMATTING_LOCALE_MAP[language] ?? language;
  }
  return typeof navigator !== 'undefined'
    ? [...navigator.languages]
    : DEFAULT_FORMATTING_LOCALE;
}

function currentI18nLanguage(): string {
  return i18n.resolvedLanguage ?? i18n.language ?? '';
}

/**
 * Resolve the locale list for Intl date/number formatting.
 * Prefers the app language set via i18next over the browser default so that
 * table cells, filter labels, and other formatted values match the UI language.
 */
export function getFormattingLocales(): string | string[] {
  return resolveFormattingLocales(currentI18nLanguage());
}

/**
 * Single-string variant for APIs that accept only one locale (e.g. Mantine's
 * `locale` prop). Returns the first locale from {@link getFormattingLocales}.
 */
export function getFormattingLocale(): string {
  const locales = getFormattingLocales();
  return Array.isArray(locales)
    ? (locales[0] ?? DEFAULT_FORMATTING_LOCALE)
    : locales;
}

/**
 * Reactive variant of {@link getFormattingLocales} for components.
 * Subscribes to i18n so locales update when the app language is applied
 * after first paint or changed in settings.
 */
export function useFormattingLocales(
  override?: string | string[]
): string | string[] {
  const { i18n: i18nInstance } = useTranslation();
  const language =
    i18nInstance?.resolvedLanguage ?? i18nInstance?.language ?? '';
  return useMemo(
    () => override ?? resolveFormattingLocales(language),
    [override, language]
  );
}

/**
 * Reactive variant of {@link getFormattingLocale} for components.
 */
export function useFormattingLocale(override?: string): string {
  const locales = useFormattingLocales(override);
  return Array.isArray(locales)
    ? (locales[0] ?? DEFAULT_FORMATTING_LOCALE)
    : locales;
}
